import { useState } from 'react'
import apiClient from '@/services/api'

import { useErrorHandler } from './useErrorHandler'

interface ResetPasswordRequest {
  token: string
  new_password: string
  confirm_password: string
}

interface ResetPasswordResponse {
  message: string
}

interface UseResetPasswordState {
  data: Nullable<ResetPasswordResponse>
  isLoading: boolean
  error: Nullable<string>
  isSuccess: boolean
}

interface UseResetPasswordReturn extends UseResetPasswordState {
  handleSubmit: (
    token: string,
    password: string,
    confirmPassword: string
  ) => Promise<void>
  reset: () => void
}

/**
 * Custom hook for managing reset password state
 * Handles password reset with token
 */
export function useResetPassword(): UseResetPasswordReturn {
  const { handleError } = useErrorHandler()
  const [state, setState] = useState<UseResetPasswordState>({
    data: null,
    isLoading: false,
    error: null,
    isSuccess: false,
  })

  const resetPassword = async (
    token: string,
    password: string,
    confirmPassword: string
  ): Promise<ResetPasswordResponse> => {
    const response = await apiClient.post<ResetPasswordResponse>(
      '/auth/reset-password',
      {
        token,
        new_password: password,
        confirm_password: confirmPassword,
      } as ResetPasswordRequest
    )
    return response.data
  }

  const handleSubmit = async (
    token: string,
    password: string,
    confirmPassword: string
  ): Promise<void> => {
    setState(prev => ({
      ...prev,
      isLoading: true,
      error: null,
      isSuccess: false,
    }))

    try {
      const response = await resetPassword(token, password, confirmPassword)

      setState(prev => ({
        ...prev,
        data: response,
        isLoading: false,
        isSuccess: true,
      }))
    } catch (error) {
      const errorMessage = handleError(error)
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
        isSuccess: false,
      }))
    }
  }

  const reset = () => {
    setState({ data: null, isLoading: false, error: null, isSuccess: false })
  }

  return {
    ...state,
    handleSubmit,
    reset,
  }
}
